import { ok } from '@atcute/client';
import type { ResolvedActor } from '@atcute/identity-resolver';
import type { ResourceUri } from '@atcute/lexicons/syntax';
import type { OAuthUserAgent } from '@atcute/oauth-browser-client';

import { WATCH_COLLECTION } from './constants';
import { getRpc } from './client';
import { createAuthRpc, resolveActor } from './identity';
import { parseAtUri, type RecordCreationResult } from './records';

export interface WatchRecordValue {
	$type: typeof WATCH_COLLECTION;
	repo: ResourceUri;
	createdAt: string;
}

export interface WatchRecord {
	uri: ResourceUri;
	cid: string;
	rkey: string;
	value: WatchRecordValue;
}

export const listWatchRecords = async (actorIdentifier: string | ResolvedActor): Promise<WatchRecord[]> => {
	const actor = typeof actorIdentifier === 'string' ? await resolveActor(actorIdentifier) : actorIdentifier;
	const rpc = getRpc(actor.pds);
	const records: WatchRecord[] = [];
	let cursor: string | undefined;

	do {
		const page = await ok(
			rpc.get('com.atproto.repo.listRecords', {
				params: {
					repo: actor.did,
					collection: WATCH_COLLECTION,
					limit: 100,
					cursor,
					reverse: true,
				},
			}),
		);

		records.push(
			...page.records.map((record) => ({
				uri: record.uri,
				cid: record.cid,
				rkey: parseAtUri(record.uri).rkey,
				value: record.value as unknown as WatchRecordValue,
			})),
		);
		cursor = page.cursor;
	} while (cursor);

	return records.filter((record) => record.value?.repo?.startsWith('at://'));
};

export const findWatchRecord = (records: WatchRecord[], repoUri: ResourceUri): WatchRecord | undefined =>
	records.find((record) => record.value.repo === repoUri);

export const createWatch = async (
	agent: OAuthUserAgent,
	repoUri: ResourceUri,
): Promise<RecordCreationResult> => {
	const rpc = createAuthRpc(agent);
	const result = await ok(
		rpc.post('com.atproto.repo.createRecord', {
			input: {
				repo: agent.sub,
				collection: WATCH_COLLECTION,
				record: {
					$type: WATCH_COLLECTION,
					repo: repoUri,
					createdAt: new Date().toISOString(),
				},
			},
		}),
	);

	return {
		uri: result.uri,
		rkey: parseAtUri(result.uri).rkey,
	};
};

export const deleteWatch = async (agent: OAuthUserAgent, rkey: string): Promise<void> => {
	const rpc = createAuthRpc(agent);
	await ok(
		rpc.post('com.atproto.repo.deleteRecord', {
			input: {
				repo: agent.sub,
				collection: WATCH_COLLECTION,
				rkey,
			},
		}),
	);
};
